import { CSAT_SAMPLE_RATE_DEFAULT } from "@redotech/redo-support-common/csat-sampling";
import type { CsatSurveyVariant } from "@redotech/redo-support-settings-common/csat-survey";
import {
  CSAT_SURVEY_METHODS,
  CSAT_SURVEY_SEND_TIME_OPTIONS,
  CsatSurveyMethod,
  DEFAULT_SURVEY_EMAIL_BODY_HTML,
  SurveySendTimeOptions,
  type CsatSurveySendTime,
} from "@redotech/redo-support-settings-common/csat-survey";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import {
  arrayEqual,
  optionalEqual,
  stringEqual,
  urlEqual,
} from "@redotech/util/equal";
import { csatVariantEqual } from "./csat-variants";

const CSAT_SURVEY_METHOD_SET: ReadonlySet<string> = new Set<string>(
  CSAT_SURVEY_METHODS,
);
const CSAT_SURVEY_SEND_TIME_SET: ReadonlySet<string> = new Set<string>(
  CSAT_SURVEY_SEND_TIME_OPTIONS,
);

const isCsatSurveyMethod = (value: string): value is CsatSurveyMethod =>
  CSAT_SURVEY_METHOD_SET.has(value);

const isCsatSurveySendTime = (value: string): value is CsatSurveySendTime =>
  CSAT_SURVEY_SEND_TIME_SET.has(value);

export const toCsatSurveyMethods = (
  methods: readonly string[] | null | undefined,
): CsatSurveyMethod[] => (methods ?? []).filter(isCsatSurveyMethod);

export const toCsatSurveySendTime = (
  value: string | null | undefined,
): CsatSurveySendTime =>
  value && isCsatSurveySendTime(value) ? value : SurveySendTimeOptions.ONE_HOUR;

export const csatForm = groupInput({
  csatEnabled: input<boolean>(),
  surveySendTime: input<CsatSurveySendTime>(),
  surveyMethods: input<CsatSurveyMethod[]>({ equal: arrayEqual(stringEqual) }),
  allowRemarks: input<boolean>(),
  headerImage: input<URL | undefined>({ equal: optionalEqual(urlEqual) }),
  bodyContent: input<string>(),
  sampleRate: input<number>(),
  allowedInboxEmails: input<string[]>({ equal: arrayEqual(stringEqual) }),
  emailTemplateId: input<string | undefined>(),
  abTestEnabled: input<boolean>(),
  abTestVariants: input<CsatSurveyVariant[]>({
    equal: arrayEqual(csatVariantEqual),
  }),
});

export type CsatValue = InputSchema.Value<typeof csatForm>;

/** The per-field inputs each tab reads and writes. */
export type CsatInputs = InputSchema.Input<typeof csatForm>["inputs"];

export const csatDefault: CsatValue = {
  csatEnabled: false,
  surveySendTime: SurveySendTimeOptions.ONE_HOUR,
  surveyMethods: [],
  allowRemarks: true,
  headerImage: undefined,
  bodyContent: DEFAULT_SURVEY_EMAIL_BODY_HTML,
  sampleRate: CSAT_SAMPLE_RATE_DEFAULT,
  allowedInboxEmails: [],
  emailTemplateId: undefined,
  abTestEnabled: false,
  abTestVariants: [],
};
